import { useMemo } from 'react'
import { Loader2, Search } from 'lucide-react'
import ToolCard from './ToolCard'
import type { ToolCatalogItem, ToolFilters, ViewMode } from '@/types/plugins'

interface ToolCatalogProps {
  tools: ToolCatalogItem[]
  filters: ToolFilters
  viewMode: ViewMode
  loading: boolean
  onTryTool: (tool: ToolCatalogItem) => void
}

export default function ToolCatalog({
  tools,
  filters,
  viewMode,
  loading,
  onTryTool,
}: ToolCatalogProps) {
  // Apply filters and sorting
  const filteredTools = useMemo(() => {
    let result = [...tools]

    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase()
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(search) ||
          t.description.toLowerCase().includes(search) ||
          t.plugin.toLowerCase().includes(search)
      )
    }

    // Plugin filter
    if (filters.plugin) {
      result = result.filter((t) => t.plugin === filters.plugin)
    }

    // Danger level filter
    if (filters.dangerLevel) {
      result = result.filter((t) => t.dangerLevel === filters.dangerLevel)
    }

    // Satisfaction filter
    if (filters.minSatisfaction !== null) {
      result = result.filter(
        (t) =>
          t.satisfaction !== null && t.satisfaction >= filters.minSatisfaction!
      )
    }

    // Sorting
    result.sort((a, b) => {
      let comparison = 0

      switch (filters.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'calls':
          comparison = a.totalCalls - b.totalCalls
          break
        case 'satisfaction': {
          const aScore = a.satisfaction ?? -1
          const bScore = b.satisfaction ?? -1
          comparison = aScore - bScore
          break
        }
        case 'danger': {
          const dangerOrder = { SAFE: 0, LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 }
          comparison = dangerOrder[a.dangerLevel] - dangerOrder[b.dangerLevel]
          break
        }
      }

      return filters.sortOrder === 'asc' ? comparison : -comparison
    })

    return result
  }, [tools, filters])

  if (loading && tools.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    )
  }

  if (filteredTools.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <Search className="w-12 h-12 text-text-muted/30 mb-4" />
        <h3 className="text-lg font-medium text-text mb-2">No tools found</h3>
        <p className="text-sm text-text-muted max-w-md">
          {filters.search
            ? `No tools match "${filters.search}". Try adjusting your search or filters.`
            : 'No tools match the current filters. Try clearing some filters.'}
        </p>
      </div>
    )
  }

  if (viewMode === 'list') {
    return (
      <div className="space-y-2 p-1">
        {filteredTools.map((tool) => (
          <ToolCard
            key={tool.name}
            tool={tool}
            onTryIt={onTryTool}
            compact
          />
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-1">
      {filteredTools.map((tool) => (
        <ToolCard
          key={tool.name}
          tool={tool}
          onTryIt={onTryTool}
        />
      ))}
    </div>
  )
}
